  <template>
    <div v-show="show">
      <b-modal v-model="showDeleteFileModal"
        title="Remover Arquivo"
        class="modal-primary"
        @ok="removeFile">

        Deseja remover o arquivo do processo? Ao confirmar essa ação o item não poderá ser recuperado.
      </b-modal>

      <b-card title="Processo" header-tag="header" footer-tag="footer">
        <div slot="header" class="row mb-0 align-items-right">
          <div class="col-12 text-right">
            <div class="switch-container">
              <span class="switch-label mr-1">
                Status
              </span>

              <toggle-button class="mb-0"
                v-model="form.status"
                :width="70"
                :height="25"
                :labels="{checked: 'Ativo', unchecked: 'Inativo'}"/>
            </div>
          </div>
        </div>

        <div class="row">
          <b-form-group class="col-12 col-md-4"
            label="Imagem"
            label-for="media">

            <single-media-uploader
              name="file"
              placeholder="static/img/defaults/upload.png"
              @success="handleUploadMedia"
              :url="mediaUrl"
              :source="mediaInstance">
            </single-media-uploader>
          </b-form-group>

          <div class="col-12 col-md-8">
            <div class="row">

              <b-form-group class="col-12 col-md-6"
                label="Tipo"
                label-for="type_id">

                <multiselect v-model="selectedType"
                  @select="updateForm('type_id', arguments[0])"
                  placeholder="Selecione o tipo"
                  :allowEmpty="true"
                  :options="types"
                  label="name"
                  :showLabels="false"
                  track-by="id"
                  :class="{
                    'is-invalid': submitted && form.errors.has('type_id'),
                    'is-valid': submitted && !form.errors.has('type_id')
                  }" />
              </b-form-group>

              <b-form-group class="col-12 col-sm-6"
                label="* Nome"
                label-for="company_name"
                description="">

                <b-form-input id="company_name"
                  v-model="form.company_name"
                  placeholder="Nome da Empresa"
                  required
                  :class="{
                    'is-invalid': submitted && form.errors.has('company_name'),
                    'is-valid': submitted && !form.errors.has('company_name')
                  }"
                  @input="slugify">
                </b-form-input>
              </b-form-group>

              <b-form-group class="col-12 col-md-6"
                label="Slug"
                label-for="slug"
                description="Se vazia será gerada uma slug automática">

                <b-form-input id="slug"
                  v-model="form.slug"
                  :class="{
                    'is-invalid': submitted && form.errors.has('slug'),
                    'is-valid': submitted && !form.errors.has('slug')
                  }"
                  @change="userEditedSlug = true">
                </b-form-input>
              </b-form-group>

              <b-form-group class="col-12 col-md-6"
                label="Data"
                label-for="date">

                <date-picker v-model="form.date"
                  :config="datepickerOptions"
                  @dp-change="updateDate"
                  :class="{
                    'is-invalid': submitted && form.errors.has('date'),
                    'is-valid': submitted && !form.errors.has('date')
                  }"/>
              </b-form-group>

              <b-form-group class="col-12"
                label="Descrição"
                label-for="description"
                description="(Obrigatório)">

                <rich-textarea v-model="form.description"
                  :height="200"
                  name="description"
                  :toolbar="[
                    ['font', ['bold', 'italic', 'underline', 'clear']],
                    // ['fontsize', ['fontsize']],
                    // ['para', ['ul', 'ol', 'paragraph']],
                    // ['color', ['color']],
                    // ['insert', ['link', 'hr']]
                    ['insert', ['link']]
                  ]"
                  ></rich-textarea>
              </b-form-group>
            </div>
          </div>
        </div>
        <slot name="footer"></slot>
      </b-card>

      <b-card title="Documentos recebidos"
        header-tag="header"
        footer-tag="footer"
        :sort-desc.sync="submissionDefaultSort"
        v-if="instance.id">

        <b-row align-h="end">
          <b-col cols="6">
            <b-form-group horizontal>
              <b-input-group>
                <b-form-input v-model="submissionFilter" placeholder="Digite para buscar"/>

                <b-input-group-append>
                  <b-btn :disabled="!submissionFilter" @click="submissionFilter = ''">
                    Limpar
                  </b-btn>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>

        <p v-if="get(form, 'submissions', []).length == 0">
          Nenhum envio foi realizado até esse momento.
        </p>

        <b-table v-else
          striped
          hover
          table-w-hover
          :current-page="submissionPagination.current"
          :per-page="submissionPagination.perPage"
          :fields="submissionFields"
          :items="form.submissions"
          :filter="submissionFilter"
          @filtered="onFiltered">

          <template slot="id" slot-scope="{ item, toggleDetails }">
            <span @click.stop.prevent="toggleDetails">
              #{{ item.id }}
            </span>
          </template>

          <template slot="user_name" slot-scope="{ item, toggleDetails }">
            <span @click.stop.prevent="toggleDetails">
              {{ get(item, 'user.name', '') }} <small>({{ get(item, 'user.main_role.name', '') }})</small>
            </span>
          </template>

          <template slot="type_name" slot-scope="{ item, toggleDetails }">
            <span @click.stop.prevent="toggleDetails">
              {{ get(item, 'type.name', '') }}
            </span>
          </template>

          <template slot="reference_date" slot-scope="{ item, toggleDetails }">
            <span @click.stop.prevent="toggleDetails">
              {{ item.reference_date | moment('MM/YYYY') }}
            </span>
          </template>

          <template slot="created_at" slot-scope="{ item, toggleDetails }">
            <span @click.stop.prevent="toggleDetails">
              {{ item.created_at | moment('DD/MM/YYYY hh:mm') }}
            </span>
          </template>

          <template slot="actions" slot-scope="{ item, toggleDetails }">
            <span class="mr-1" v-b-tooltip.hover :title="get(item, 'attachments', []).length + ' anexos'">
              <i class="fa fa-paperclip"></i>
            </span>

            <a v-if="get(item, '_links', false)"
              class="mr-1"
              :href="get(item, '_links.download', '#!')"
              v-b-tooltip.hover title="Baixar todos">

              <i class="fa fa-download"></i>
            </a>

            <a class="mr-1" @click.stop.prevent="toggleDetails" v-b-tooltip.hover title="Detalhes">
              <i class="fa fa-info-circle"></i>
            </a>

            <a href="" @click.stop.prevent="confirmDeleteFile(item)" v-b-tooltip.hover title="Remover">
              <i class="fa fa-trash-o"></i>
            </a>
          </template>

          <template slot="row-details" slot-scope="{ item, toggleDetails }">
            <b-row v-if="item.observations">
              <b-col>
                <b>Observações:</b> {{ item.observations }}
              </b-col>
            </b-row>

            <b-row>
              <b-col>
                <fieldset class="attachments" v-if="item.attachments.length">
                  <legend align="left">
                    <b>Download</b>
                  </legend>

                  <span v-for="(attachment, index) in item.attachments" :key="attachment.id">

                    <a :href="get(attachment, '_links.download', '#!')" class="col-lg-6">
                      {{ attachment.file.original_name }}
                    </a>

                    <a v-if="get(item, '_links', false)"
                      :href="get(item, '_links.download', '#!')"
                      class="col-lg-6"
                      v-b-tooltip.hover title="Baixar todos">
                      <i class="fa fa-download"></i> Baixar todos
                    </a>

                    <!-- <span v-if="index < item.attachments.length - 1">, </span> -->
                  </span>
                </fieldset>
              </b-col>
            </b-row>
          </template>
        </b-table>

        <div class="pagination-module">
          <b-row align-h="end">
            <b-col md="6" class="my-1">
              <b-pagination class="my-0 float-right"
                v-model="submissionPagination.current"
                :total-rows="submissionPagination.total"
                :per-page="submissionPagination.perPage"/>
            </b-col>
          </b-row>
        </div>
      </b-card>

      <b-card header-tag="header" footer-tag="footer" v-if="instance.id">
        <div slot="header"  class="row">
          <div class="col">
            <h1 slot="header">
              Categorias
            </h1>
          </div>

          <div class="col" v-if="nestedCreateRoute">
            <router-link :to="nestedCreateRoute">
              <label class="btn btn-primary float-right" style="margin: 4px 0px 0px;">
                <i class="fa fa-plus mr-1"></i> Incluir
              </label>
            </router-link>
          </div>
        </div>

        <sortable :entity="categoryEntity"
          :http="categoryHttp"
          :filter="{ process_id: instance.id }"
          :card="false"></sortable>
      </b-card>

      <b-card title="Seguidores" header-tag="header" footer-tag="footer" v-if="instance.id">
        <p v-if="get(form, 'followers', []).length == 0">
          Nenhum seguidor neste processo.
        </p>

        <b-table v-else striped hover :fields="followersFields" :items="followers"></b-table>
      </b-card>

      <b-card title="Popup" header-tag="header" footer-tag="footer">
        <div slot="header" class="row mb-0 align-items-right">
          <div class="col-12 text-right">
            <div class="switch-container">
              <span class="switch-label mr-1">
                Status
              </span>

              <toggle-button class="mb-0"
                v-model="form.popup_status"
                :width="70"
                :height="25"
                :labels="{checked: 'Ativo', unchecked: 'Inativo'}"/>
            </div>
          </div>
        </div>

        <div class="row">

          <div class="col-12">
            <div class="row">

              <b-form-group class="col-12 col-sm-12"
                label="Título do Popup"
                label-for="popup_title"
                description="">

                <b-form-input id="popup_title"
                  v-model="form.popup_title"
                  placeholder="Título"
                  :class="{
                    'is-invalid': submitted && form.errors.has('popup_title'),
                    'is-valid': submitted && !form.errors.has('popup_title')
                  }"
                  @input="slugify">
                </b-form-input>
              </b-form-group>

              <b-form-group class="col-12"
                label="Texto do Popup"
                label-for="popup_description"
                description="">

                <rich-textarea v-model="form.popup_description"
                  :height="200"
                  name="popup_description"
                  :toolbar="[
                    ['font', ['bold', 'italic', 'underline', 'clear']],
                    ['insert', ['link']]
                  ]"
                  ></rich-textarea>
              </b-form-group>
            </div>
          </div>
        </div>
        <slot name="footer"></slot>
      </b-card>
    </div>
  </template>

  <script>
  import slugify from 'slugify'
  import { isEmpty, isObject, get, set } from 'lodash'
  import moment from 'moment'
  import { datepickerOptions, slugifyOptions } from '@/plugins'
  import { resolveHTTP } from '@/services/api'
  import { blProcessCategories } from '@/_entities'

  export default {
    props: {
      form: {
        type: [Object],
        required: true
      },
      submitted: {
        type: Boolean,
        default: false
      },
      instance: {
        type: Object,
        default () {
          return {
            media: []
          }
        }
      },
      http: {
        type: Object,
        required: true
      }
    },
    data () {
      return {
        locks: {
          types: true
        },
        selectedType: null,
        types: [],
        show: true,
        showDeleteFileModal: false,
        deletableFile: null,
        userEditedSlug: false,

        description: '',

        media: null,

        datepickerOptions: Object.assign(datepickerOptions, {
          format: 'MM/YYYY'
        }),

        followersFields: {
          name: {
            sortable: true,
            label: 'Nome'
          },
          email: {
            sortable: false
          },
          main_role_name: {
            label: 'Perfil'
          }
        },

        // Categories
        categoryEntity: blProcessCategories,
        categoryHttp: null,

        submissionFilter: '',
        submissionPagination: {
          current: 1,
          perPage: 10,
          total: 0
        },
        submissionDefaultSort: 'created_at',
        submissionFields: [
          {
            key: 'id',
            sortable: false,
            label: 'Código'
          },
          {
            key: 'user_name',
            sortable: true,
            label: 'Enviado por'
          },
          {
            key: 'type_name',
            sortable: false,
            label: 'Categoria'
          },
          {
            key: 'reference_date',
            sortable: true,
            label: 'Data Referência'
          },
          {
            key: 'created_at',
            sortable: true,
            label: 'Recebido em'
          },
          {
            key: 'actions',
            label: 'Ações'
          }
        ],

        empty: {
          company_name: '',
          slug: '',
          description: '',
          date: '',
          type_id: '',
          media_ids: [],
          followers: [{
            name: '',
            email: '',
            main_role: {
              name: ''
            }
          }],
          status: true,
          popup_title: '',
          popup_description: '',
          popup_status: true
        }
      }
    },

    watch: {
      '$props': {
        handler: function (newProps) {
          if (!get(newProps, 'form.type_id', false)) {
            this.selectedType = null
          }
        },
        deep: true
      },
      description (next) {
        set(this.form, 'description', next)
      }
    },

    computed: {
      ready () {
        return !(this.locks.types)
      },

      mediaUrl () {
        return this.http.routes.uploadMedia(this.instance.id)
      },

      mediaInstance () {
        if (!isEmpty(this.media)) {
          return this.media
        }

        if (!isEmpty(this.instance.media)) {
          return this.instance.media[0]
        }

        return {}
      },

      followers () {
        return get(this.form, 'followers', []).map(function (follower) {
          follower.main_role_name = get(follower, 'main_role.name', '')

          return follower
        })
      },

      submissions () {
        return get(this.form, 'submissions', []).map(function (submission) {
          submission._showDetails = false

          return submission
        })
      },

      nestedCreateRoute () {
        return {
          name: 'manageables.nested.create',
          params: {
            entity: 'bl-processes',
            id: this.form.id,
            nestedEntity: 'bl-process-categories'
          }
        }
      }
    },
    methods: {
      get,
      set,

      updateForm (path, value, valuePath) {
        set(this.form, path, isObject(value) ? get(value || {}, valuePath || 'id', null) : value)
      },

      updateDate ({ date }) {
        this.form.date = date ? date.format('MM/YYYY') : null
      },

      handleUploadMedia (file, response, progress) {
        this.form.media_ids = [response.id]
        this.media = response
      },

      confirmDeleteFile (file) {
        this.deletableFile = file
        this.showDeleteFileModal = true
      },

      removeFile () {
        const self = this
        this.$Progress.start()
        self.showDeleteFileModal = false

        if (self.deletableFile === null) {
          return null
        }

        self.processSubmissionsHttp.destroy(self.deletableFile.id)
          .then(function (res) {
            self.$toasted.success('Envio removido com sucess')
            self.deletableFile = null
            self.$emit('refresh')
          })
      },

      onFiltered (filteredItems) {
        this.submissionPagination.total = filteredItems.length
        this.submissionPagination.current = 1
      },

      slugify () {
        const self = this

        if (this.userEditedSlug) {
          return
        }

        self.show = false
        self.form.slug = slugify(self.form.company_name, slugifyOptions)
        self.$nextTick(() => {
          self.show = true
        })
      }
    },
    created () {
      const self = this

      resolveHTTP('/bl/process-types').index({ params: { _onepage: true } })
        .then(function (res) {
          self.types = res.data.collection
          self.locks.types = false

          self.selectedType = self.form.type_id
            ? self.types.find((o) => o.id === self.form.type_id)
            : null
        })

      self.processSubmissionsHttp = resolveHTTP('/bl/process-submissions')

      if (self.instance.id) {
        self.form.withData(this.instance).setInitialValues(self.instance)
        self.form.type_id = self.instance.type_id || null
        let m = moment(self.instance.date, 'YYYY-MM-DD HH:ii:ss')
        self.form.date = m.format('MM/YYYY')
        self.description = self.instance.description
      } else {
        self.form.withData(this.empty).setInitialValues(this.empty)
      }

      self.categoryHttp = self.categoryEntity.api || resolveHTTP(self.categoryEntity.url)
    }
  }
  </script>

  <style lang="scss" scoped>

    fieldset.attachments {
      border: solid 1px #DDD !important;
      padding: 0 10px 10px 10px;
      border-bottom: none;

      legend {
        width: auto !important;
        padding:0 10px; /* To give a bit of padding on the left and right */
        border: none;
        font-size: inherit;
      }
    }
  </style>



// WEBPACK FOOTER //
// src/components/forms/Bl/Process.vue